import { useState } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Group,
  Modal,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  EXPAND_BY,
  ITEM_TYPES,
  MEASUREMENT_KINDS,
  MEASUREMENT_SPECS,
  hasCapability,
  type ExpandBy,
  type ItemType,
  type MeasurementKind,
} from '@prooffleet/contracts';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { ApiError, api, useSession } from '@prooffleet/client';
import { EmptyState, PageHeader, TableSkeleton } from '../components/Page.js';

interface DraftItem {
  key: string;
  label: string;
  type: ItemType;
  expandBy: ExpandBy;
  measurementKind: MeasurementKind | null;
  photoRequired: boolean;
  critical: boolean;
}

interface DraftSection {
  key: string;
  label: string;
  items: DraftItem[];
}

interface TemplateRow {
  _id: string;
  key: string;
  labels: Record<string, string>;
  appliesTo: string;
  version: number;
  publishedAt: string;
  sections: {
    key: string;
    labels: Record<string, string>;
    items: {
      key: string;
      labels: Record<string, string>;
      type: ItemType;
      expandBy: ExpandBy;
      measurementKind: MeasurementKind | null;
      photoRequired: boolean;
      critical: boolean;
    }[];
  }[];
}

const slug = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 64);

function emptyItem(): DraftItem {
  return {
    key: '',
    label: '',
    type: 'boolean',
    expandBy: 'none',
    measurementKind: null,
    photoRequired: false,
    critical: false,
  };
}

/**
 * Редактор шаблонов осмотра.
 *
 * Шаблон неизменяем: сохранение публикует НОВУЮ версию, а записи осмотров
 * держат вмороженную копию той версии, которую видел водитель (правило 2).
 * Поэтому кнопка называется «Publish new version», а не «Save» — иначе
 * пользователь ждёт правки на месте.
 *
 * Развёртка (`expandBy`) — то, ради чего шаблон вообще конфигурируемый:
 * один пункт «глубина протектора» превращается в десять пунктов у тягача 6x4
 * и в восемь у прицепа, и позиции берутся из конфигурации осей самой машины.
 */
export function Templates(): React.JSX.Element {
  const actor = useSession((s) => s.actor);
  const canWrite = actor !== null && hasCapability(actor, 'template:write');
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);
  const [key, setKey] = useState('');
  const [label, setLabel] = useState('');
  const [appliesTo, setAppliesTo] = useState<string | null>('tractor');
  const [sections, setSections] = useState<DraftSection[]>([]);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['templates'],
    queryFn: () => api<{ templates: TemplateRow[] }>('/templates'),
  });

  const templates = data?.templates ?? [];

  const publish = useMutation({
    mutationFn: () =>
      api('/templates', {
        method: 'POST',
        body: {
          key,
          labels: { en: label },
          appliesTo,
          sections: sections.map((s) => ({
            key: s.key === '' ? slug(s.label) : s.key,
            labels: { en: s.label },
            items: s.items.map((i) => ({
              key: i.key === '' ? slug(i.label) : i.key,
              labels: { en: i.label },
              type: i.type,
              expandBy: i.expandBy,
              measurementKind: i.type === 'measurement' ? i.measurementKind : null,
              photoRequired: i.photoRequired,
              critical: i.critical,
            })),
          })),
        },
      }),
    onSuccess: async () => {
      setOpen(false);
      await queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : 'Could not publish the template');
    },
  });

  const startFrom = (template: TemplateRow | null): void => {
    setError(null);
    if (template === null) {
      setKey('');
      setLabel('');
      setAppliesTo('tractor');
      setSections([{ key: '', label: '', items: [emptyItem()] }]);
    } else {
      setKey(template.key);
      setLabel(template.labels['en'] ?? template.key);
      setAppliesTo(template.appliesTo);
      setSections(
        template.sections.map((s) => ({
          key: s.key,
          label: s.labels['en'] ?? s.key,
          items: s.items.map((i) => ({
            key: i.key,
            label: i.labels['en'] ?? i.key,
            type: i.type,
            expandBy: i.expandBy,
            measurementKind: i.measurementKind,
            photoRequired: i.photoRequired,
            critical: i.critical,
          })),
        })),
      );
    }
    setOpen(true);
  };

  const patchItem = (si: number, ii: number, changes: Partial<DraftItem>): void => {
    setSections((prev) =>
      prev.map((s, i) =>
        i !== si ? s : { ...s, items: s.items.map((it, j) => (j === ii ? { ...it, ...changes } : it)) },
      ),
    );
  };

  const incomplete =
    key.trim() === '' ||
    label.trim() === '' ||
    sections.length === 0 ||
    sections.some((s) => s.label.trim() === '' || s.items.length === 0) ||
    sections.some((s) =>
      s.items.some(
        (i) => i.label.trim() === '' || (i.type === 'measurement' && i.measurementKind === null),
      ),
    );

  return (
    <Stack gap={0}>
      <PageHeader
        title="Templates"
        count={isLoading ? undefined : templates.length}
        hint="Checklists are versioned and immutable. Publishing creates a new version; inspections already filed keep the version the driver actually saw."
        action={
          canWrite ? (
            <Button
              leftSection={<IconPlus size={15} stroke={1.8} />}
              onClick={() => startFrom(null)}
            >
              New template
            </Button>
          ) : undefined
        }
      />

      {isLoading && <TableSkeleton rows={2} cols={4} />}

      {!isLoading && templates.length === 0 && (
        <EmptyState
          title="No templates yet"
          hint="A template decides which components the driver is asked about and which of them need a measurement."
        />
      )}

      <Stack gap={0}>
        {templates.map((template) => (
          <div
            key={template._id}
            style={{ borderBottom: '1px solid var(--pf-line)', padding: '14px 16px' }}
          >
            <Group justify="space-between" align="flex-start" wrap="nowrap" gap="lg">
              <div style={{ minWidth: 0 }}>
                <Group gap={8} align="baseline">
                  <Text fw={640} fz="sm">
                    {template.labels['en'] ?? template.key}
                  </Text>
                  <Text className="pf-mono">{template.key}</Text>
                  <Badge variant="light" color="gray" size="xs">
                    v{template.version}
                  </Badge>
                </Group>
                <Text fz="sm" c="gray.7" mt={2}>
                  {template.appliesTo} · {template.sections.length} sections ·{' '}
                  {template.sections.reduce((n, s) => n + s.items.length, 0)} items
                </Text>
                <Text className="pf-mono" mt={4}>
                  published {new Date(template.publishedAt).toLocaleString()}
                </Text>
              </div>
              <Button
                className="pf-row-action"
                size="compact-xs"
                variant="default"
                disabled={!canWrite}
                style={{ flex: '0 0 auto' }}
                onClick={() => startFrom(template)}
              >
                New version
              </Button>
            </Group>
          </div>
        ))}
      </Stack>

      <Modal opened={open} onClose={() => setOpen(false)} title="Publish template version" size="xl">
        <Stack gap="md">
          <Alert color="blue">
            Publishing never edits an existing version. Inspections keep a frozen copy of the
            checklist the driver saw, so history stays readable after every change.
          </Alert>

          <Group grow align="flex-start">
            <TextInput
              label="Name"
              value={label}
              onChange={(e) => {
                setLabel(e.currentTarget.value);
                if (key === '') setKey(slug(e.currentTarget.value));
              }}
              placeholder="Tractor pre-trip inspection"
            />
            <TextInput
              label="Key"
              description="Stable identifier, kept across versions"
              value={key}
              onChange={(e) => setKey(slug(e.currentTarget.value))}
            />
            <Select
              label="Applies to"
              data={[
                { value: 'tractor', label: 'Tractor' },
                { value: 'trailer', label: 'Trailer' },
              ]}
              value={appliesTo}
              onChange={setAppliesTo}
              allowDeselect={false}
            />
          </Group>

          {sections.map((section, si) => (
            <Card key={si} withBorder padding="sm">
              <Group justify="space-between" mb="xs" wrap="nowrap">
                <TextInput
                  placeholder="Section name, e.g. Tires"
                  value={section.label}
                  style={{ flex: 1 }}
                  onChange={(e) =>
                    setSections((prev) =>
                      prev.map((s, i) =>
                        i === si ? { ...s, label: e.currentTarget.value } : s,
                      ),
                    )
                  }
                />
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  aria-label="Remove section"
                  onClick={() => setSections((prev) => prev.filter((_, i) => i !== si))}
                >
                  <IconTrash size={15} stroke={1.6} />
                </ActionIcon>
              </Group>

              <Table className="pf-table">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th w="26%">Item</Table.Th>
                    <Table.Th w="16%">Type</Table.Th>
                    <Table.Th w="18%">Measurement</Table.Th>
                    <Table.Th w="16%">Repeat for</Table.Th>
                    <Table.Th w="10%">Photo</Table.Th>
                    <Table.Th w="10%">Critical</Table.Th>
                    <Table.Th w="4%" />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {section.items.map((item, ii) => (
                    <Table.Tr key={ii}>
                      <Table.Td>
                        <TextInput
                          size="xs"
                          placeholder="Tread depth"
                          value={item.label}
                          onChange={(e) => patchItem(si, ii, { label: e.currentTarget.value })}
                        />
                      </Table.Td>
                      <Table.Td>
                        <Select
                          size="xs"
                          data={ITEM_TYPES.map((t) => ({ value: t, label: t }))}
                          value={item.type}
                          allowDeselect={false}
                          onChange={(v) =>
                            patchItem(si, ii, {
                              type: v ?? 'boolean',
                              measurementKind: v === 'measurement' ? item.measurementKind : null,
                            })
                          }
                        />
                      </Table.Td>
                      <Table.Td>
                        <Select
                          size="xs"
                          placeholder="—"
                          disabled={item.type !== 'measurement'}
                          data={MEASUREMENT_KINDS.map((k) => ({
                            value: k,
                            label: MEASUREMENT_SPECS[k].label,
                          }))}
                          value={item.measurementKind}
                          onChange={(v) =>
                            patchItem(si, ii, { measurementKind: v })
                          }
                        />
                      </Table.Td>
                      <Table.Td>
                        <Select
                          size="xs"
                          data={EXPAND_BY.map((e) => ({
                            value: e,
                            label: e === 'none' ? 'once' : e,
                          }))}
                          value={item.expandBy}
                          allowDeselect={false}
                          onChange={(v) => patchItem(si, ii, { expandBy: v ?? 'none' })}
                        />
                      </Table.Td>
                      <Table.Td>
                        <Checkbox
                          checked={item.photoRequired}
                          onChange={(e) =>
                            patchItem(si, ii, { photoRequired: e.currentTarget.checked })
                          }
                        />
                      </Table.Td>
                      <Table.Td>
                        <Checkbox
                          checked={item.critical}
                          onChange={(e) => patchItem(si, ii, { critical: e.currentTarget.checked })}
                        />
                      </Table.Td>
                      <Table.Td>
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          aria-label="Remove item"
                          onClick={() =>
                            setSections((prev) =>
                              prev.map((s, i) =>
                                i !== si ? s : { ...s, items: s.items.filter((_, j) => j !== ii) },
                              ),
                            )
                          }
                        >
                          <IconTrash size={15} stroke={1.6} />
                        </ActionIcon>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>

              <Button
                size="compact-xs"
                variant="subtle"
                mt="xs"
                leftSection={<IconPlus size={13} stroke={1.8} />}
                onClick={() =>
                  setSections((prev) =>
                    prev.map((s, i) => (i === si ? { ...s, items: [...s.items, emptyItem()] } : s)),
                  )
                }
              >
                Add item
              </Button>
            </Card>
          ))}

          <Button
            variant="default"
            leftSection={<IconPlus size={15} stroke={1.8} />}
            onClick={() => setSections((prev) => [...prev, { key: '', label: '', items: [emptyItem()] }])}
          >
            Add section
          </Button>

          {error !== null && <Alert color="red">{error}</Alert>}

          <Button loading={publish.isPending} disabled={incomplete} onClick={() => publish.mutate()}>
            Publish new version
          </Button>
        </Stack>
      </Modal>
    </Stack>
  );
}
